/**
 * Session 19b — OCPP 2.0.1 Device Model registry + helpers.
 *
 * `STANDARD_VARIABLES_V201` is the static definition (the seed + the
 * source of truth for static metadata like `rebootRequired`). The Dexie
 * `variableValues` table stores the mutable per-station VALUES; static
 * facts are looked up here by `(component, variable)` rather than
 * duplicated into every row.
 */
import { STANDARD_VARIABLES_V201, type V201VariableDef } from './variables';

export {
  STANDARD_VARIABLES_V201,
  UNQUALIFIED,
  DEFAULT_ATTRIBUTE_TYPE,
  flatKeyFor,
  type V201VariableDef,
  type V201Mutability,
} from './variables';

/**
 * One seeded Device Model row, sans the static-only `rebootRequired`
 * flag. Structurally matches the persistence `VariableValueSnapshot`;
 * the state layer (useConfigurationStore) — which is allowed to import
 * persistence — passes it straight to the repository. Kept persistence-
 * type-free so this `protocol/` module honours the layer boundary.
 */
export type SeedSnapshot = Omit<V201VariableDef, 'rebootRequired'>;

/** The seed payload for a fresh v2.0.1 station's `variableValues` table. */
export function seedSnapshots(): readonly SeedSnapshot[] {
  return STANDARD_VARIABLES_V201.map((d) => ({
    component: d.component,
    evseId: d.evseId,
    connectorId: d.connectorId,
    variable: d.variable,
    attributeType: d.attributeType,
    value: d.value,
    mutability: d.mutability,
    dataType: d.dataType,
    ...(d.unit !== undefined ? { unit: d.unit } : {}),
    section: d.section,
  }));
}

const BY_COMPONENT_VARIABLE = new Map<string, V201VariableDef>(
  STANDARD_VARIABLES_V201.map((d) => [`${d.component}.${d.variable}`, d]),
);

/** Static definition for a `(component, variable)` pair, or null if not standard. */
export function lookupDef(component: string, variable: string): V201VariableDef | null {
  return BY_COMPONENT_VARIABLE.get(`${component}.${variable}`) ?? null;
}

/** True when a SetVariables write to this variable should reply RebootRequired. */
export function requiresReboot(component: string, variable: string): boolean {
  return lookupDef(component, variable)?.rebootRequired ?? false;
}
